import { Renderer } from '../../RichTextEditor';
import { useFields } from './Fields.Context';
import { useCurrentUser } from 'hooks/useCurrentUser';
import type { Styles } from 'generated/graphql';
import { useReporterStyles } from 'hooks/useReporterStyles';
import { UnifiedEditor } from '../../RichTextEditor/ui/UnifiedEditor';
import { telemetryContextFor } from 'modules/Apollo/types';
import { Stack } from 'common/ui/Layout';

export const FieldsAddendumRenderer = ({
  isClaimedByMe,
}: {
  isClaimedByMe: boolean;
}): React.ReactElement | null => {
  const [{ finalizedAddendums, addendum }] = useFields();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterSettings = data?.me?.reporterSettings;

  const style: Styles = useReporterStyles(reporterSettings);

  const hasNoFinalizedAddendums = !finalizedAddendums || finalizedAddendums.some((a) => a == null);
  const hasUnclaimedDraftAddendum = !isClaimedByMe && addendum != null;

  // https://sironamedical.atlassian.net/browse/EN-2417
  if (hasNoFinalizedAddendums && !hasUnclaimedDraftAddendum) {
    return null;
  }

  return (
    <Stack vertical space="small">
      {finalizedAddendums.map((addendum, index) => (
        <UnifiedEditor
          key={index}
          isAddendumEditor={true}
          isDisabled={true}
          variant="view"
          label={`Addendum #${index + 1}`}
          addendumOwner={
            addendum.ownerFirstName || addendum.ownerLastName
              ? { firstName: addendum.ownerFirstName ?? '', lastName: addendum.ownerLastName ?? '' }
              : undefined
          }
          addendumSubmittedAt={addendum.submittedAt}
        >
          <Renderer
            content={Array.isArray(addendum.content) ? addendum.content : [addendum.content]}
            style={style}
          />
        </UnifiedEditor>
      ))}
      {hasUnclaimedDraftAddendum && (
        <UnifiedEditor
          isAddendumEditor={true}
          isDisabled={true}
          variant="view"
          label="Draft Addendum"
        >
          <Renderer
            content={Array.isArray(addendum?.content) ? addendum?.content : [addendum?.content]}
            style={style}
          />
        </UnifiedEditor>
      )}
    </Stack>
  );
};

FieldsAddendumRenderer.displayName = 'FieldsAddendumRenderer';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(FieldsAddendumRenderer);
